/**
 * Dev Mode Toggle
 * 
 * Provides quick switching between view modes and experience styles.
 * Positioned in the right-side header group, to the left of the share button.
 * Only visible when devMode is enabled.
 */

import { useState } from 'react';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { usePreferencesStore, type ExperienceStylePreference } from '@/lib/store/preferencesStore';
import type { ViewMode } from '@/lib/views/types';
import { Code2, Check, FolderOpen, Palette, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useActiveDetailPage } from '@/contexts/ActiveDetailPageContext';
import { useAutoFill } from '@/lib/dev/autofill';
import { useAuthStore } from '@/lib/store/authStore';

// Users who get dev mode in production — add emails here
const DEV_MODE_USERS = ['john@zaviago.com', 'Administrator'];

export function DevModeToggle() {
  const navigate = useNavigate();
  const devMode = usePreferencesStore((state) => state.devMode);
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);
  const setViewMode = usePreferencesStore((state) => state.setViewMode);
  const setExperienceStyle = usePreferencesStore((state) => state.setExperienceStyle);
  const dashboardStateOverride = usePreferencesStore((state) => state.dashboardStateOverride);
  const setDashboardStateOverride = usePreferencesStore((state) => state.setDashboardStateOverride);
  const chatwootStateOverride = usePreferencesStore((state) => state.chatwootStateOverride);
  const setChatwootStateOverride = usePreferencesStore((state) => state.setChatwootStateOverride);
  const signupIntentOverride = usePreferencesStore((state) => state.signupIntentOverride);
  const setSignupIntentOverride = usePreferencesStore((state) => state.setSignupIntentOverride);
  const username = useAuthStore((state) => state.username);

  const [isOpen, setIsOpen] = useState(false);

  // Get active detail page for autofill
  const { api: activePage } = useActiveDetailPage();
  const { runAutoFill } = useAutoFill();

  // Show dev mode in development builds OR for allowlisted production users
  const isAllowedUser = DEV_MODE_USERS.includes(username ?? '');
  if (!devMode && !isAllowedUser) {
    return null;
  }

  const viewModeOptions: { value: ViewMode; label: string }[] = [
    { value: 'experience', label: 'Experience' },
    { value: 'system', label: 'System' },
  ];

  const styleOptions: { value: ExperienceStylePreference; label: string }[] = [
    { value: 'auto', label: 'Auto (Recommended)' },
    { value: 'shopify', label: 'Shopify-style' },
    { value: 'woocommerce', label: 'WooCommerce-style' },
    { value: 'erpnext', label: 'ERPNext-style' },
    { value: 'apple', label: 'Apple-style' },
    { value: 'custom', label: 'Custom' },
  ];

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    setIsOpen(false);
    // Page will automatically update via store subscription
  };

  const handleStyleChange = (style: ExperienceStylePreference) => {
    setExperienceStyle(style);
    setIsOpen(false);
  };

  const dashboardStateOptions: { value: 'auto' | 'setup' | 'onboarding' | 'active'; label: string }[] = [
    { value: 'auto', label: 'Auto (Real State)' },
    { value: 'setup', label: 'Setup (Not Provisioned)' },
    { value: 'onboarding', label: 'Onboarding (First Steps)' },
    { value: 'active', label: 'Active (Status Hub)' },
  ];

  const handleDashboardStateChange = (state: 'auto' | 'setup' | 'onboarding' | 'active') => {
    setDashboardStateOverride(state);
    setIsOpen(false);
  };

  const chatwootStateOptions: { value: 'auto' | 'no_account' | 'empty' | 'has_conversations'; label: string }[] = [
    { value: 'auto', label: 'Auto (Real Data)' },
    { value: 'no_account', label: 'No Account (Marketing CTA)' },
    { value: 'empty', label: 'Empty (No Conversations)' },
    { value: 'has_conversations', label: 'Has Conversations' },
  ];

  const handleChatwootStateChange = (state: 'auto' | 'no_account' | 'empty' | 'has_conversations') => {
    setChatwootStateOverride(state);
    setIsOpen(false);
  };

  const signupIntentOptions: { value: 'auto' | 'chat' | 'selling' | 'pos' | 'crm'; label: string }[] = [
    { value: 'auto', label: 'Auto (Real Intent)' },
    { value: 'chat', label: 'Chat (Multi-Channel)' },
    { value: 'selling', label: 'Selling (E-Commerce)' },
    { value: 'pos', label: 'POS (Point of Sale)' },
    { value: 'crm', label: 'CRM (Sales Pipeline)' },
  ];

  const handleSignupIntentChange = (intent: 'auto' | 'chat' | 'selling' | 'pos' | 'crm') => {
    setSignupIntentOverride(intent);
    // Also force dashboard to onboarding state so the intent page shows
    if (intent !== 'auto') {
      setDashboardStateOverride('onboarding');
      // Clear the onboarding completion flag so the intent page renders
      localStorage.removeItem('onboarding_completed_at');
    }
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {/* Avoid `asChild` here to prevent Radix composeRefs ref loops */}
      <DropdownMenuTrigger
        type="button"
        aria-label="Dev Mode - Switch View Mode & Style"
        title="Dev Mode - Switch View Mode & Style"
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          'rounded-full nav-glass shadow-apple-sm h-11 w-11 !border-[1.5px] !border-white hover:scale-105 active:scale-95 transition-all duration-300 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 touch-manipulation bg-blue-500 hover:bg-blue-600'
        )}
      >
        <Code2 className="h-5 w-5 text-white" />
      </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Dev Mode Controls</DropdownMenuLabel>
          
          <DropdownMenuSeparator />
          
          {/* View Mode Selection */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            View Mode
          </DropdownMenuLabel>
          {viewModeOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleViewModeChange(option.value)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{option.label}</span>
              {viewMode === option.value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          
          {/* Experience Style Selection */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Experience Style
          </DropdownMenuLabel>
          {styleOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleStyleChange(option.value)}
              className="flex items-center justify-between cursor-pointer"
              disabled={viewMode === 'system'} // Disable when in system mode
            >
              <span className={cn(viewMode === 'system' && 'text-muted-foreground')}>
                {option.label}
              </span>
              {experienceStyle === option.value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />

          {/* Dashboard State Override */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Dashboard State
          </DropdownMenuLabel>
          {dashboardStateOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleDashboardStateChange(option.value)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{option.label}</span>
              {dashboardStateOverride === option.value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />

          {/* Chatwoot Chat Card State Override */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Chat Card State
          </DropdownMenuLabel>
          {chatwootStateOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleChatwootStateChange(option.value)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{option.label}</span>
              {chatwootStateOverride === option.value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />

          {/* Signup Intent Override (Onboarding Preview) */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Signup Intent (Onboarding)
          </DropdownMenuLabel>
          {signupIntentOptions.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleSignupIntentChange(option.value)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{option.label}</span>
              {signupIntentOverride === option.value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />

          {/* Current Status */}
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            <div>Mode: <span className="font-medium">{viewMode}</span></div>
            {viewMode === 'experience' && (
              <div>Style: <span className="font-medium">{experienceStyle}</span></div>
            )}
            <div>Dashboard: <span className="font-medium">{dashboardStateOverride}</span></div>
            <div>Chat: <span className="font-medium">{chatwootStateOverride}</span></div>
            <div>Intent: <span className="font-medium">{signupIntentOverride}</span></div>
          </div>
          
          <DropdownMenuSeparator />
          
          {/* Dev Tools */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Dev Tools
          </DropdownMenuLabel>
          
          <DropdownMenuItem
            onClick={() => {
              setIsOpen(false);
              navigate('/dev/review-sessions');
            }}
            className="cursor-pointer"
          >
            <FolderOpen className="mr-2 h-4 w-4" />
            <span>Review Sessions</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem
            onClick={() => {
              setIsOpen(false);
              navigate('/dev/pricing-rule-card-playground');
            }}
            className="cursor-pointer"
          >
            <Palette className="mr-2 h-4 w-4" />
            <span>Pricing Rule Card Playground</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {/* Auto-fill Actions */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Auto-fill
          </DropdownMenuLabel>
          
          <DropdownMenuItem
            onClick={async () => {
              if (!activePage || !activePage.isReady) {
                return;
              }
              setIsOpen(false);
              
              const meta = activePage.getMeta();
              if (!meta) {
                console.warn("[DevModeToggle] Cannot autofill: meta not loaded");
                return;
              }
              
              try {
                await runAutoFill(
                  activePage.doctype,
                  meta,
                  activePage.getFormData,
                  activePage.applyPatch,
                  activePage.applyTablePatch,
                  { rowsOverride: 5 }
                );
                if (import.meta.env.DEV) {
                  console.log(`[DevModeToggle] Auto-filled ${activePage.doctype}`);
                }
              } catch (error) {
                console.error("[DevModeToggle] Auto-fill failed:", error);
              }
            }}
            disabled={!activePage || !activePage.isReady}
            className="cursor-pointer"
          >
            <Zap className="mr-2 h-4 w-4" />
            <span>Auto-fill (5 rows)</span>
          </DropdownMenuItem>
          
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={!activePage || !activePage.isReady}>
              <Zap className="mr-2 h-4 w-4" />
              <span>Auto-fill (N rows)</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {[1, 3, 5, 10].map((numRows) => (
                <DropdownMenuItem
                  key={numRows}
                  onClick={async () => {
                    if (!activePage || !activePage.isReady) {
                      return;
                    }
                    setIsOpen(false);
                    
                    const meta = activePage.getMeta();
                    if (!meta) {
                      console.warn("[DevModeToggle] Cannot autofill: meta not loaded");
                      return;
                    }
                    
                    try {
                      await runAutoFill(
                        activePage.doctype,
                        meta,
                        activePage.getFormData,
                        activePage.applyPatch,
                        activePage.applyTablePatch,
                        { rowsOverride: numRows }
                      );
                      if (import.meta.env.DEV) {
                        console.log(`[DevModeToggle] Auto-filled ${activePage.doctype} with ${numRows} rows`);
                      }
                    } catch (error) {
                      console.error("[DevModeToggle] Auto-fill failed:", error);
                    }
                  }}
                  className="cursor-pointer"
                >
                  <span>{numRows} row{numRows !== 1 ? 's' : ''}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
  );
}

